import React, { useEffect, useState } from 'react'
import {
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    FbButton,
    FbFormikCheckbox,
    FbFormikTextField,
    GridContainer,
    GridItem,
    Icons,
    Typography,
} from '@fabricks/ui-components'
import { Breadcrumbs, Grid } from '@material-ui/core'
import { useKeycloak } from 'react-keycloak'
import { RouteComponentProps, useHistory } from 'react-router-dom'
import { get as _get } from 'lodash'
import { Field, Form, Formik, FormikHelpers, FormikValues, useFormikContext } from 'formik'
import {
    FlowRackBoxDto,
    RackConfigDto,
    StorageConfigDto,
    StorageConfigUpdateInput, useDeleteBoxTypeMutation, useDeleteStorageConfigsMutation,
    useUpdateStorageConfigQueryMutation
} from '../../../api/models'
import { RectSkeleton } from '../../../helper/skeleton'
import Navigation, { getDynamicRouteGetters as dynamicRoutes } from '../../../constants/navigation'
import {
    createAdditionalFormElement,
    EditStorageFormConfig,
    EditStorageFormConfigInitialValues,
} from './EditStorageFormConfig'
import { FormGenerator, FormGeneratorConfig } from '../../../utils/formGenerator'
import { useCurrentBoxesPerRack, useCurrentStorageconfig, useStorageconfigs } from '../../../services/dataService'
import {
    FbCheckboxFormElement,
    FbFormikSlider,
    FbSliderFormElement,
    FbTextFieldFormElement
} from '../../../utils/formElements'
import {
    CustomerGroupFormField,
    PickZoneSelectFormElement,
    PickZoneSelectFormField,
} from '../../Optimization/StorageLocationOptimization/StorageLocationOptimization'
import { getContext, useRackPerZone } from '../../../services/apiService'
import { extractError } from '../../../utils/apiUtils'
import { showError, showSuccess, showWarn } from '../../../utils/sweetAlertHelper'
import { hasRole } from "../../../services/developerAccessService";
import { DeleteButtonProps } from '../Boxtypes/Edit'

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface EditStorageConfigProps extends RouteComponentProps<any> {
}

interface Values {
    firstName: string
    lastName: string
    email: string
}

export const SubmitButton = (props: any) => {
    const { isSubmitting } = useFormikContext()
    return (
        <FbButton disabled={isSubmitting} variant="contained" color="primary" type="submit" style={{ float: 'right' }}>
            Save and next
        </FbButton>
    )
}

const ZoneFormikTextField = (props: any) => {
    return <FbFormikTextField {...props} label="Zone" />
}

const ZoneEffFactorFormField = (props: any) => {
    const configs = {
        marks: true,
        showValueLabel: 'on',
        key: 'zone_eff_factor',
        min: 1,
        max: 20,
        step: 0.1,
    }

    return <FbFormikSlider {...props} label="Zone Efficiency" {...configs} />
}

const PickzoneDistanceFormikTextField = (props: any) => {
    return <FbFormikTextField {...props} type="number" label="Distance to pickzone zero in cm" />
}

const IsFlowRackCheckboxField = (props: any) => {
    return <FbFormikCheckbox label="Flow rack" {...props} />
}

const RackConfigurator = (props: any) => {
    const [keycloak] = useKeycloak()
    const { values } = useFormikContext()
    const { data, loading, error } = useRackPerZone(keycloak.token, { zone: values.name })

    const generateFormFields = () => {
        const racks = _get(data, 'getRacksPerZone.data', [])
        if (racks.length > 0) {
            const renderedBoxes = racks.map((rackName: any, rackIndex: number) => {
                return (
                    <Field
                        key={`rack-${rackName.uuid}`}
                        name={`racks.${rackIndex}.rack-${rackName.uuid}`}
                        label={`Width of rack ${rackName.value} in cm`}
                        type="number"
                        component={FbFormikTextField}
                    />
                )
            })

            return (
                <>
                    <Typography variant="h6" style={{ marginTop: '12px' }}>
                        Rack configuration
                    </Typography>
                    {renderedBoxes}
                </>
            )
        }
        return null
    }
    return generateFormFields()
}

const FlowRackActiveNegotiator = (props: any) => {
    const [keycloak] = useKeycloak()
    const { values } = useFormikContext()
    const { boxesData, boxesLoading, boxesError } = useCurrentBoxesPerRack(keycloak.token, { uuid: values.name })

    const generateFormFields = () => {
        const possibleBoxes = _get(boxesData, 'getBoxesPerZone.data', [])
        if (possibleBoxes.length > 0 && values.isFlowRack) {
            const renderedBoxes = possibleBoxes.map((box: any, boxIndex: number) => {
                return (
                    <Field
                        name={`flowBoxes.${box.uuid}`}
                        placeholder={box.name}
                        label={box.name}
                        component={FbFormikTextField}
                    />
                )
            })

            return <>{renderedBoxes}</>
        }
        return null
    }
    return generateFormFields()
}

export const EditStorageConfig = (props: EditStorageConfigProps) => {
    const { match } = props
    const [keycloak] = useKeycloak()
    const history = useHistory()
    const { data: zoneConfigs, error: zoneErrors, loading: zoneLoading } = useStorageconfigs(keycloak.token, {
        page: 0,
        size: 1000,
    })
    const [formConfig, setFormConfig] = useState<FormGeneratorConfig>(EditStorageFormConfig)
    const [isDataLoaded, setDataLoaded] = useState(false)
    const {
        data: storageConfigData,
        error: storageConfigError,
        loading: storageConfigLoading,
    } = useCurrentStorageconfig(keycloak.token, { uuid: match.params.uuid })
    const [updateStorageConfig, updateStorageConfigResult] = useUpdateStorageConfigQueryMutation()
    const [executeDeleteMutation, resultOfDeleteMutation] = useDeleteStorageConfigsMutation()
    const [initialValues, setInitialValues] = useState(EditStorageFormConfigInitialValues)

    const currentZoneConfigs = _get(zoneConfigs, 'getStorageConfigs.data', [])
    const currentZoneConfigsByUuids = currentZoneConfigs.map((storageConfig: StorageConfigDto) => {
        return storageConfig.uuid
    })
    const currentIndex = currentZoneConfigsByUuids.indexOf(match.params.uuid)
    const nextUuid = currentZoneConfigsByUuids[currentIndex+1 % currentZoneConfigsByUuids.length]

    const createAdditionalFields = () => {
        const t1 = createAdditionalFormElement(formConfig)('flowBox1', {
            component: FbTextFieldFormElement,
            childProps: {
                label: `Box (1)`,
                type: 'number',
            },
        })
        const t2 = createAdditionalFormElement(t1)('flowBox2', {
            component: FbTextFieldFormElement,
            childProps: {
                label: `Box (2)`,
                type: 'number',
            },
        })
        setFormConfig(t2)
        return null
    }

    useEffect(() => {
        const extractedData = _get(storageConfigData, 'getStorageConfig.data.0', null) as StorageConfigDto
        if (extractedData) {
            setInitialValues(
                Object.assign(EditStorageFormConfigInitialValues, {
                    name: extractedData.zone,
                    zoneEffFactor: _get(extractedData, 'zoneEffFactor', 1),
                    pickzone: _get(extractedData, 'currentPickZone.next', ''),
                    pickzoneDistance: _get(extractedData, 'currentPickZone.distance', 0),
                    customerGroups: extractedData.customerGroups,
                    isFlowRack: extractedData.isFlowRackZone,
                })
            )

            createAdditionalFields()
        }
        return () => {
            setTimeout(() => setDataLoaded(true), 1000)
        }
    }, [createAdditionalFields, storageConfigData])

    const renderTitle = () => {
        if (!isDataLoaded) {
            return <RectSkeleton />
        }
        const extractedData = _get(storageConfigData, 'getStorageConfig.data.0', null) as StorageConfigDto

        return <CardTitle align="left" title={`Edit of ${extractedData.zone}`} variant="h3" padding="0px" />
    }

    const onDelete = () => {
        const extractedData = _get(storageConfigData, 'getStorageConfig.data.0', null) as StorageConfigDto
        executeDeleteMutation({
            context: getContext(keycloak.token || ""),
            variables: {
                id: extractedData.uuid
            }
        }).then((response) => {
            const error = extractError(response, 'deleteStorageConfig')
            if (error) {
                return showError(_get(error, 'message', 'unknown'), _get(error, 'code', 'XXX'))
            }
            showSuccess(
                'The zone configuration was successfully deleted',
                `Deleted`
            ).then(() => {
                return history.push(Navigation.Storageconfigs.path)
            })
        }).catch((err) => {
            showError('Error', err)
        })
    }

    const DeleteButton = (props: DeleteButtonProps) => {
        const { accept, reject } = props
        const { isSubmitting, values } = useFormikContext()
        const onDelete = () => {
            showWarn(`Do you really want to delete the storage config for the zone ${values.name}?`, `Deletion of ${values.name}`, {
                confirmButtonText: 'Yes, delete it!',
                showCancelButton: true,
                confirmButtonColor: '#d33',
            }).then((res) => {
                if (res.value) {
                    return accept()
                }
                return reject()
            })
        }

        return (
            <FbButton
                disabled={isSubmitting}
                variant="contained"
                color="default"
                onClick={onDelete}
                style={{ float: 'left' }}
            >
                Delete
            </FbButton>
        )
    }

    const renderMainContent = () => {
        if (!isDataLoaded) {
            return Object.keys(initialValues).map(() => <RectSkeleton />)
        }

        const getInitialValues = (_storageConfigData: any): FormikValues => {
            const extractedData = _get(_storageConfigData, 'getStorageConfig.data.0', null) as StorageConfigDto
            if (extractedData) {
                return {
                    name: extractedData.zone,
                    nextPickzone: _get(extractedData, 'currentPickZone.next', '') || '',
                    distance: _get(extractedData, 'currentPickZone.distance', 0),
                    bestPosition: extractedData.bestPosition,
                    zoneEffFactor: _get(extractedData, 'zoneEffFactor', 1),
                    customerGroups: extractedData.customerGroups,
                    isFlowRack: extractedData.isFlowRackZone,
                    // @ts-ignore
                    flowBoxes: _get(extractedData, 'flowRackBoxes', []).reduce((a: any, box: FlowRackBoxDto) => {
                        if (box && box.box && box.box.uuid) {
                            a[box.box.uuid] = box.count
                        }
                        return a
                    }, {}),
                    // @ts-ignore
                    racks: _get(extractedData, 'racks', []).map((rack: RackConfigDto) => {
                        const rackObj = {}
                        // @ts-ignore
                        rackObj[`rack-${rack.name}`] = rack.count
                        return rackObj
                    }),
                }
            }
            return {}
        }

        return (
            <Formik
                key={match.params.uuid}
                enableReinitialize
                initialValues={getInitialValues(storageConfigData)}
                onSubmit={(values, actions) => {
                    const assignedRoles = _get(keycloak, 'tokenParsed.resource_access', {})
                    if (!hasRole(assignedRoles, 'admin')) {
                        return showError('You have not enough rights for this action', '')
                    }

                    const extractedData = _get(storageConfigData, 'getStorageConfig.data.0', null) as StorageConfigDto
                    const transformValuesToUpdateDto = (_values: any): StorageConfigUpdateInput => {
                        const flowBoxes = _get(_values, 'flowBoxes', {})
                        return {
                            bestPosition: _get(_values, 'bestPosition', null),
                            currentPickZone: {
                                next: _get(_values, 'nextPickzone', null),
                                distance: _get(_values, 'distance', null),
                            },
                            zoneEffFactor:  _get(_values, 'zoneEffFactor', null),
                            isFlowRackZone: _get(_values, 'isFlowRack', null),
                            customerGroups: _get(_values, 'customerGroups', null),
                            flowRackBoxes: Object.keys(flowBoxes).map((key: string) => {
                                return { boxUuid: key, count: flowBoxes[key] }
                            }),
                            racks: _get(_values, 'racks', []).map((rack: any) => {
                                const objectKey = Object.keys(rack)[0]
                                const name = objectKey.split('rack-')[1]
                                return { name, count: rack[objectKey] }
                            }),
                        }
                    }

                    if (extractedData && extractedData.uuid) {
                        updateStorageConfig({
                            context: getContext(keycloak.token),
                            variables: {
                                id: extractedData.uuid,
                                config: transformValuesToUpdateDto(values),
                            },
                        })
                            .then(response => {
                                const error = extractError(response, 'updateStorageConfig')
                                if (error) {
                                    return showError(_get(error, 'message', 'unknown'), _get(error, 'code', 'XXX'))
                                }
                                showSuccess(
                                    'The configuration was successfully processed',
                                    `Zone ${extractedData.zone} updated`
                                ).then(() => {
                                    setDataLoaded(false)
                                    if (nextUuid) {
                                        return history.push(dynamicRoutes().pages.editStorageconfigs(nextUuid))
                                    }
                                    return history.push(Navigation.Storageconfigs.path)
                                })
                            })
                            .catch(err => {
                                showError('Error', err)
                            })
                            .finally(() => {
                                actions.setSubmitting(false)
                            })
                    }
                }}
                render={() => (
                    <Form>
                        <Field name="name" placeholder="Zone" component={ZoneFormikTextField} />
                        <CustomerGroupFormField
                            name="customerGroups"
                            label="Picking groups"
                            childProps={{ multiple: true }}
                        />
                        <ZoneEffFactorFormField name="zoneEffFactor" />
                        <RackConfigurator />
                        <Typography variant="h6" style={{ marginTop: '12px' }}>
                            {' '}
                            Pickzone assignment
                            {' '}
                        </Typography>
                        <PickZoneSelectFormField name="nextPickzone" label="Rack belongs to pickzone" childProps={{}} />
                        <Field
                            name="distance"
                            placeholder="Distance to Pickzone Zero in cm"
                            component={PickzoneDistanceFormikTextField}
                        />
                        <Field
                            name="bestPosition"
                            placeholder="Best position"
                            label="Best position"
                            component={FbFormikTextField}
                        />
                        <Typography variant="h6" style={{ marginTop: '12px' }}>
                            Flow rack configuration
                        </Typography>
                        <Field name="isFlowRack" placeholder="Is flow rack" component={IsFlowRackCheckboxField} />
                        <FlowRackActiveNegotiator />
                        <SubmitButton />
                        <DeleteButton accept={onDelete} reject={() => {}} />
                    </Form>
                )}
            />
        )

        /* return (
            <FormGenerator
                id="fuel-form"
                config={formConfig}
                submitLabel="Save"
                initialValues={initialValues}
                onSubmit={onSubmit}
            />
        ) */
    }

    return (
        <GridContainer>
            <GridItem xs={12} sm={12} md={12}>
                <Card className="card">
                    <CardHeader isStats={false} hasIcon type="plain">
                        <div style={{ margin: '2em 0px 2em 0px' }}>
                            <Breadcrumbs aria-label="breadcrumb">
                                <Grid
                                    container
                                    direction="row"
                                    alignItems="center"
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => {
                                        history.push('/masterdata/administration/storageconfigs')
                                    }}
                                >
                                    <Grid item>
                                        <Icons.ArrowBackIos />
                                    </Grid>
                                    <Grid item>
                                        <span>Go back</span>
                                    </Grid>
                                </Grid>
                            </Breadcrumbs>
                        </div>
                        <div>{renderTitle()}</div>
                    </CardHeader>
                    <CardBody isProfile>{renderMainContent()}</CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    )
}
